import { useState, useMemo, useEffect } from 'react';
import {
  ChevronRight,
  ChevronDown,
  File,
  Folder,
  FolderOpen,
  Search,
  RefreshCw,
  Plus,
  MoreVertical,
  FileCode,
  FileJson,
  FileText,
  FileImage,
  Trash2,
  Edit2,
  Copy,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { Dropdown } from '@/components/ui/Dropdown';

interface FileNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  children?: FileNode[];
  size?: number;
  modified?: string;
}

interface RecentlyChangedFile {
  path: string;
  timestamp: Date;
}

interface FileExplorerProps {
  files: FileNode[];
  selectedFile?: string;
  recentlyChangedFiles?: RecentlyChangedFile[];
  onSelectFile: (path: string) => void;
  onCreateFile?: (path: string) => void;
  onCreateFolder?: (path: string) => void;
  onDeleteFile?: (path: string) => void;
  onRenameFile?: (path: string, newName: string) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
  className?: string;
}

export function FileExplorer({
  files,
  selectedFile,
  recentlyChangedFiles = [],
  onSelectFile,
  onCreateFile,
  onCreateFolder,
  onDeleteFile,
  onRenameFile,
  onRefresh,
  isLoading = false,
  className,
}: FileExplorerProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['/']));
  const [searchQuery, setSearchQuery] = useState('');

  // Create a set of recently changed file paths for quick lookup
  const recentlyChangedPaths = useMemo(() => {
    const now = Date.now();
    const RECENT_THRESHOLD = 10000; // 10 seconds
    return new Set(
      recentlyChangedFiles
        .filter((f) => now - f.timestamp.getTime() < RECENT_THRESHOLD)
        .map((f) => f.path)
    );
  }, [recentlyChangedFiles]);

  // Auto-expand folders containing recently changed files
  useEffect(() => {
    if (recentlyChangedFiles.length > 0) {
      const newExpanded = new Set(expandedFolders);
      recentlyChangedFiles.forEach((file) => {
        // Expand parent folders
        const parts = file.path.split('/');
        let currentPath = '';
        for (let i = 0; i < parts.length - 1; i++) {
          currentPath += (i > 0 ? '/' : '') + parts[i];
          if (currentPath) {
            newExpanded.add(currentPath);
          }
        }
      });
      setExpandedFolders(newExpanded);
    }
  }, [recentlyChangedFiles]);

  const toggleFolder = (path: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const filteredFiles = useMemo(() => {
    if (!searchQuery) return files;

    const filterRecursive = (nodes: FileNode[]): FileNode[] => {
      return nodes.reduce<FileNode[]>((acc, node) => {
        if (node.name.toLowerCase().includes(searchQuery.toLowerCase())) {
          acc.push(node);
        } else if (node.children) {
          const filteredChildren = filterRecursive(node.children);
          if (filteredChildren.length > 0) {
            acc.push({ ...node, children: filteredChildren });
          }
        }
        return acc;
      }, []);
    };

    return filterRecursive(files);
  }, [files, searchQuery]);

  return (
    <div className={cn('flex flex-col h-full bg-white dark:bg-dark-800', className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-dark-700">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Files</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-500 dark:text-gray-400 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
          </button>
          <Dropdown
            trigger={
              <button className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-500 dark:text-gray-400">
                <Plus className="w-4 h-4" />
              </button>
            }
            items={[
              {
                id: 'new-file',
                label: 'New File',
                icon: <File className="w-4 h-4" />,
                onClick: () => onCreateFile?.('/'),
              },
              {
                id: 'new-folder',
                label: 'New Folder',
                icon: <Folder className="w-4 h-4" />,
                onClick: () => onCreateFolder?.('/'),
              },
            ]}
          />
        </div>
      </div>

      {/* Search */}
      <div className="p-2 border-b border-gray-200 dark:border-dark-700">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-50 dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* File Tree */}
      <div className="flex-1 overflow-y-auto p-2">
        {filteredFiles.map((node) => (
          <FileTreeNode
            key={node.path}
            node={node}
            depth={0}
            expandedFolders={expandedFolders}
            selectedFile={selectedFile}
            recentlyChangedPaths={recentlyChangedPaths}
            onToggleFolder={toggleFolder}
            onSelectFile={onSelectFile}
            onDeleteFile={onDeleteFile}
            onRenameFile={onRenameFile}
          />
        ))}
      </div>
    </div>
  );
}

function FileTreeNode({
  node,
  depth,
  expandedFolders,
  selectedFile,
  recentlyChangedPaths,
  onToggleFolder,
  onSelectFile,
  onDeleteFile,
  onRenameFile,
}: {
  node: FileNode;
  depth: number;
  expandedFolders: Set<string>;
  selectedFile?: string;
  recentlyChangedPaths: Set<string>;
  onToggleFolder: (path: string) => void;
  onSelectFile: (path: string) => void;
  onDeleteFile?: (path: string) => void;
  onRenameFile?: (path: string, newName: string) => void;
}) {
  const isExpanded = expandedFolders.has(node.path);
  const isSelected = selectedFile === node.path;
  const isDirectory = node.type === 'directory';
  const isRecentlyChanged = recentlyChangedPaths.has(node.path);

  const handleClick = () => {
    if (isDirectory) {
      onToggleFolder(node.path);
    } else {
      onSelectFile(node.path);
    }
  };

  const contextMenuItems = [
    {
      id: 'rename',
      label: 'Rename',
      icon: <Edit2 className="w-4 h-4" />,
      onClick: () => {
        const newName = prompt('New name:', node.name);
        if (newName && newName !== node.name) {
          onRenameFile?.(node.path, newName);
        }
      },
    },
    {
      id: 'copy-path',
      label: 'Copy Path',
      icon: <Copy className="w-4 h-4" />,
      onClick: () => navigator.clipboard.writeText(node.path),
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      danger: true,
      onClick: () => {
        if (confirm(`Delete "${node.name}"?`)) {
          onDeleteFile?.(node.path);
        }
      },
    },
  ];

  return (
    <div>
      <div
        className={cn(
          'group flex items-center gap-1 px-2 py-1 rounded cursor-pointer transition-all duration-300',
          isSelected
            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100'
            : isRecentlyChanged
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 animate-pulse'
              : 'hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-700 dark:text-gray-300'
        )}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        onClick={handleClick}
      >
        {/* Expand/Collapse Icon */}
        {isDirectory ? (
          <span className="w-4 h-4 flex items-center justify-center">
            {isExpanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </span>
        ) : (
          <span className="w-4" />
        )}

        {/* File/Folder Icon */}
        <FileIcon node={node} isExpanded={isExpanded} isNew={isRecentlyChanged} />

        {/* Name */}
        <span className="flex-1 text-sm truncate">{node.name}</span>

        {/* New Badge */}
        {isRecentlyChanged && (
          <span className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-green-500 text-white rounded-full">
            <Sparkles className="w-2.5 h-2.5" />
            New
          </span>
        )}

        {/* Context Menu */}
        <div
          className="opacity-0 group-hover:opacity-100"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            trigger={
              <button className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-dark-600">
                <MoreVertical className="w-3 h-3" />
              </button>
            }
            items={contextMenuItems}
            align="right"
          />
        </div>
      </div>

      {/* Children */}
      {isDirectory && isExpanded && node.children && (
        <div>
          {node.children.map((child) => (
            <FileTreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              expandedFolders={expandedFolders}
              selectedFile={selectedFile}
              recentlyChangedPaths={recentlyChangedPaths}
              onToggleFolder={onToggleFolder}
              onSelectFile={onSelectFile}
              onDeleteFile={onDeleteFile}
              onRenameFile={onRenameFile}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FileIcon({ node, isExpanded, isNew = false }: { node: FileNode; isExpanded: boolean; isNew?: boolean }) {
  const newClass = isNew ? 'text-green-500' : '';

  if (node.type === 'directory') {
    return isExpanded ? (
      <FolderOpen className={cn('w-4 h-4', newClass || 'text-yellow-500')} />
    ) : (
      <Folder className={cn('w-4 h-4', newClass || 'text-yellow-500')} />
    );
  }

  const ext = node.name.split('.').pop()?.toLowerCase();

  if (isNew) {
    return <FileCode className="w-4 h-4 text-green-500" />;
  }

  switch (ext) {
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
      return <FileCode className="w-4 h-4 text-blue-500" />;
    case 'json':
      return <FileJson className="w-4 h-4 text-yellow-600" />;
    case 'md':
    case 'txt':
      return <FileText className="w-4 h-4 text-gray-500" />;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'svg':
      return <FileImage className="w-4 h-4 text-purple-500" />;
    default:
      return <File className="w-4 h-4 text-gray-400" />;
  }
}
